import { useInfiniteQuery } from "@tanstack/react-query";

import { SIDEBAR_OBSERVABILITY_QUERY_OPTIONS } from "./sidebarObservabilityQueryOptions";

import { isPostgresOnlySearch } from "@/routes/request-logs/search/older-data";
import { buildStructuredSearchPayload } from "@/routes/request-logs/search/structured-search-payload";
import { useStructuredSearchStore } from "@/components/StructuredSearch/stores";
import { useAuth } from "@/context/auth-context";
import { useUser } from "@/context/user-context";
import { queryKeys } from "@/constants";
import {
  fetchStructuredRequestsPaginated,
  type PaginatedRequestsResponse,
  type UseStructuredRequestsPaginatedOptions,
} from "@/queries/requests/useStructuredRequestsPaginated";
import { REQUEST_LOGS_SIDEBAR_PAGE_SIZE } from "@/utils/requestLogUrlParams";

export const SIDEBAR_STRUCTURED_REQUEST_LOGS_SCOPE = "sidebar-structured" as const;

interface RequestLogsSearchPageParam {
  page: number;
  after_key: Record<string, unknown> | null;
}

export interface SidebarStructuredRequestLogsInfiniteData {
  pageParams: RequestLogsSearchPageParam[];
  pages: PaginatedRequestsResponse[];
}

export const getSidebarStructuredRequestLogsQueryKey = ({
  userToken,
  requestSearchParams,
  activeWorkspaceId,
  initialPageNumber,
  sidebarPageSize,
}: {
  userToken: string;
  requestSearchParams: UseStructuredRequestsPaginatedOptions;
  activeWorkspaceId: number | null | undefined;
  initialPageNumber: number;
  sidebarPageSize: number;
}) =>
  [
    queryKeys.requests,
    SIDEBAR_STRUCTURED_REQUEST_LOGS_SCOPE,
    userToken,
    requestSearchParams,
    activeWorkspaceId,
    initialPageNumber,
    sidebarPageSize,
  ] as const;

export const createEmptySidebarStructuredInfiniteData = (
  initialPageNumber: number,
  sidebarPageSize: number,
): SidebarStructuredRequestLogsInfiniteData => ({
  pageParams: [{ page: initialPageNumber, after_key: null }],
  pages: [
    {
      has_next: false,
      has_prev: false,
      items: [],
      next_num: null,
      page: initialPageNumber,
      pages: 1,
      per_page: sidebarPageSize,
      prev_num: null,
      total: 0,
      next_after_key: null,
      pagination_mode: "page",
    },
  ],
});

export const useStructuredSidebarRequestLogs = (enabledFlag = true) => {
  const structuredSearchStore = useStructuredSearchStore();
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const user = useUser();
  const activeWorkspaceId = user?.activeWorkspaceId;
  const activeWorkspace = user?.activeWorkspace;
  const initialPageNumber = structuredSearchStore.pageIndex;
  const sidebarPageSize =
    structuredSearchStore.pageSize > 0
      ? structuredSearchStore.pageSize
      : REQUEST_LOGS_SIDEBAR_PAGE_SIZE;

  const structuredSearchPayload = buildStructuredSearchPayload(
    structuredSearchStore.filters,
    structuredSearchStore.dateRange,
  );

  const requestSearchParams: UseStructuredRequestsPaginatedOptions = {
    ...(structuredSearchStore.q ? { q: structuredSearchStore.q } : {}),
    filterGroup: structuredSearchPayload.filterGroup,
    includeCount: !isPostgresOnlySearch({
      dateRange: structuredSearchStore.dateRange,
      cutoverDate: activeWorkspace?.search_cutover_date,
    }),
  };
  const queryEnabled = enabledFlag && !!userToken && !!activeWorkspaceId;

  return useInfiniteQuery({
    queryKey: getSidebarStructuredRequestLogsQueryKey({
      userToken,
      requestSearchParams,
      activeWorkspaceId,
      initialPageNumber,
      sidebarPageSize,
    }),
    queryFn: ({ pageParam }) => {
      const { page, after_key }: RequestLogsSearchPageParam =
        (pageParam as RequestLogsSearchPageParam) ?? {
          page: initialPageNumber,
          after_key: null,
        };

      return fetchStructuredRequestsPaginated({
        userToken,
        page,
        pageSize: sidebarPageSize,
        searchParams: requestSearchParams,
        workspaceId: activeWorkspaceId!,
        afterKey: after_key,
      });
    },
    getNextPageParam: (lastPage): RequestLogsSearchPageParam | undefined => {
      if (lastPage.pagination_mode === "cursor") {
        return lastPage.next_after_key
          ? { page: 1, after_key: lastPage.next_after_key }
          : undefined;
      }
      return lastPage.has_next && lastPage.next_num
        ? { page: lastPage.next_num, after_key: null }
        : undefined;
    },
    getPreviousPageParam: (firstPage): RequestLogsSearchPageParam | undefined =>
      firstPage.has_prev && firstPage.prev_num
        ? { page: firstPage.prev_num, after_key: null }
        : undefined,
    enabled: queryEnabled,
    placeholderData: createEmptySidebarStructuredInfiniteData(initialPageNumber, sidebarPageSize),
    ...SIDEBAR_OBSERVABILITY_QUERY_OPTIONS,
  });
};
